import { Box, Card, CardContent, CardHeader } from '@mui/material';
import { ArcElement, Chart, Legend, Tooltip } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { useCompositionColors } from 'src/utils/hooks/useChartColors';
import { intlFormatNumber } from 'src/utils/number';

import { CompositionItem } from '../CompositionItem';
Chart.register(ArcElement, Tooltip, Legend);

type WalletCompositionCardProps = {
  title: string;
  compositions: Array<{
    _key: string;
    value: number | string;
    color?: string;
    quotasAmount?: number;
  }>;
  onClick: () => void;
};

export const CompositionCard = ({
  title,
  compositions,
  onClick,
}: WalletCompositionCardProps) => {
  const chartColors = useCompositionColors(compositions);

  const compositionDisplay = compositions.slice(0, 4);
  const othersComposition = compositions.slice(4, compositions.length);

  if (compositions.length > 4) {
    const others = {
      _key: 'Outros',
      quotasAmount: othersComposition.reduce(
        (acc, comp) => acc + (comp.quotasAmount || 0),
        0,
      ),
      value: othersComposition.reduce(
        (acc, comp) => acc + (Number(comp.value) || 0),
        0,
      ),
    };

    compositionDisplay.push(others);
  }

  return (
    <Card
      sx={{
        width: '100%',
        px: 2,
        '& :hover': {
          cursor: 'pointer',
        },
      }}
      onClick={onClick}>
      <CardHeader title={title} />
      <CardContent
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'row',
          gap: 1,
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
        <Box
          sx={{
            width: '90%',
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            alignItems: 'baseline',
          }}>
          {compositionDisplay.map((comp, idx) => (
            <CompositionItem
              color={chartColors[idx]}
              key={idx}
              _key={comp?._key}
              value={`${intlFormatNumber(comp.value)}%`}
              showPercent={true}
            />
          ))}
        </Box>
        <Box width="49%" height={110} position="relative">
          <Doughnut
            data={{
              labels: compositionDisplay.map((comp) => comp._key),
              datasets: [
                {
                  data: compositionDisplay.map((comp) => comp.value),
                  backgroundColor: chartColors,
                },
              ],
            }}
            options={{
              plugins: {
                legend: {
                  display: false,
                },
                tooltip: {
                  callbacks: {
                    label: function (context) {
                      const label = context.label || '';
                      const value = context.parsed;
                      return `${label}: ${intlFormatNumber(value)}%`;
                    },
                  },
                },
              },
              cutout: '70%',
              responsive: true,
              maintainAspectRatio: false,
            }}
          />
        </Box>
      </CardContent>
    </Card>
  );
};
